import React, { useEffect, useRef, useState } from "react"
import { useField, useFormikContext } from "formik"
import { enqueueSnackbar } from "notistack"
import { Tooltip } from "@mui/material"

import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { ConfigQuery, fetchDimensionDetailsForUser } from "../../createOrderApi"
import { SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import { StandardErrorDialog, useStandardErrorDialogState } from "../../../dialogs/StandardErrorDialog"

import { getFormikFieldError, isAccountingDimensionKey } from "./helpers"
import { ExtendedDynamicConfigKey, DynamicConfigField } from "./formTypes"
import { handleAutoCompleteMode, useAutocompleteController } from "./useAutocompleteController"
import { useFormResetWatcher } from "./useFormResetWatcher"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null
}

function getProp(obj: unknown, key: string): unknown {
    return isRecord(obj) ? obj[key] : undefined
}

function isCostCenter(value: unknown): value is { value: string; name: string } {
    if (!isRecord(value)) return false
    return typeof value["value"] === "string" && typeof value["name"] === "string"
}

function transformData(data: unknown): Array<{ id: string; name: string }> {
    const costCenters = getProp(getProp(data, "accountDimension"), "costCenters")
    if (!Array.isArray(costCenters)) return []

    return costCenters.filter(isCostCenter).map(({ value, name }) => ({ id: value, name }))
}

export type AccountingDimensionOption = Readonly<{
    id: string
    name: string
    dimensionName?: string
}>

// Stable identities: MUI recomputes its internal input-reset and highlight effects whenever these
// change, which with an inline arrow means on every render.
const EMPTY_OPTIONS: AccountingDimensionOption[] = []
const getOptionLabel = (option: AccountingDimensionOption) => option.name || ""
const keepServerOrder = (options: AccountingDimensionOption[]) => options
const isSameOption = (option: AccountingDimensionOption | null, value: AccountingDimensionOption | null) => {
    if (!value || !option) return false
    return option.id === value.id
}

// The full cost center list is shared with `DimensionGuard` and does not change while an order is
// being created, so don't re-download it every time the dropdown is reopened.
const COST_CENTERS_STALE_TIME = 5 * 60 * 1000

type DimensionDetailsQueryParams = Readonly<{
    userId: number
    dimensionId: number
    search: string
    forceRefreshKey?: number
}>

// What the unfiltered cost center list told us, tagged with the refresh key it was read from so a
// form reset re-evaluates it.
type InitialListState = Readonly<{
    refreshKey: number
    hasSingleCostCenter: boolean
}>

function fetchDimensionDetailsForUserWithRefreshKey(
    { forceRefreshKey: _forceRefreshKey, ...params }: DimensionDetailsQueryParams,
    axios: Parameters<typeof fetchDimensionDetailsForUser>[1],
    signal?: Parameters<typeof fetchDimensionDetailsForUser>[2],
) {
    return fetchDimensionDetailsForUser(params, axios, signal)
}

type DynamicAutocompleteProps = {
    config: DynamicConfigField
    user?: SelectedMainUserState
}

function getAutocompleteQueryConfig(key: ExtendedDynamicConfigKey, user: SelectedMainUserState | undefined) {
    if (!user || !isAccountingDimensionKey(key)) return null

    return {
        userId: user.userId,
        dimensionId: Number(key.split("_")[2]),
    }
}

export function DynamicAutocomplete({ config, user }: DynamicAutocompleteProps) {
    const { l10n } = useAppLocalization()
    const { key, inputField, label, description } = config

    const [field, meta, helpers] = useField<AccountingDimensionOption | null>(key)
    const { submitCount } = useFormikContext<unknown>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)
    const wasReset = useFormResetWatcher(key)

    const mode = handleAutoCompleteMode(inputField?.mode)

    const [tooltipOpen, setTooltipOpen] = useState(false)

    const {
        open,
        debouncedSearch,
        enabled,
        isLoadOnOpen,
        isSearchPending,
        forceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    } = useAutocompleteController(mode, wasReset)

    const queryConfig = getAutocompleteQueryConfig(key, user)

    // A dimension that offers a single cost center is pre-filled and locked. That has to be decided
    // from the *unfiltered* list: deriving it from the current options would lock the field the moment
    // a search narrowed down to one match, because MUI then closes the popup and marks the input
    // read-only (`popupOpen = open && !readOnly`).
    const [initialList, setInitialList] = useState<InitialListState | null>(null)
    const isInitialListResolved = initialList !== null && initialList.refreshKey === forceRefreshKey
    const hasSingleCostCenter = isInitialListResolved && initialList.hasSingleCostCenter
    const isSearching = debouncedSearch.trim() !== ""

    const { data, isError, isFetching, isLoading, isPreviousData } = useApiQuery<
        DimensionDetailsQueryParams,
        unknown,
        AccountingDimensionOption[]
    >(fetchDimensionDetailsForUserWithRefreshKey, {
        queryName: ConfigQuery.ACCOUNT_DIMENSION_DETAILS_FOR_USER,
        dependencies: {
            userId: queryConfig?.userId ?? 0,
            dimensionId: queryConfig?.dimensionId ?? 0,
            search: debouncedSearch,
            // Dropped by `filterEmptyParams` while it is 0, so the initial key matches the one
            // `DimensionGuard` uses and both share a single cached response.
            forceRefreshKey: forceRefreshKey || undefined,
        },
        select: transformData,
        // The unfiltered list is needed on mount no matter which mode the config asks for — it decides
        // the pre-fill and the "dimension has no cost centers" handling. Once it has arrived, the query
        // is driven purely by `enabled` from useAutocompleteController.
        enabled: queryConfig !== null && (enabled || (!isInitialListResolved && !isSearching)),
        keepPreviousData: true,
        staleTime: COST_CENTERS_STALE_TIME,
    })

    const options = data ?? EMPTY_OPTIONS

    const dimensionName = label ?? description ?? ""
    const withDimensionName = (value: AccountingDimensionOption | null): AccountingDimensionOption | null =>
        value ? { ...value, dimensionName } : value

    useEffect(() => {
        if (isInitialListResolved || isSearching) return
        if (isLoading || isError || isPreviousData || data === undefined) return

        setInitialList({ refreshKey: forceRefreshKey, hasSingleCostCenter: data.length === 1 })
    }, [data, forceRefreshKey, isError, isInitialListResolved, isLoading, isPreviousData, isSearching])

    const notifiedAboutMissingCostCentersRef = useRef(false)
    const { errorDialog, closeErrorDialog, openErrorDialog } = useStandardErrorDialogState()

    useEffect(() => {
        // If API returns no cost centers (or unexpected payload shape), don't show generic "Unknown error".
        // Instead show a clear message: modal when required, snackbar when optional.
        if (notifiedAboutMissingCostCentersRef.current) return
        if (!enabled || isLoading || isError) return
        if (field.value) return
        if (debouncedSearch.trim() !== "") return
        if (options.length !== 0) return

        notifiedAboutMissingCostCentersRef.current = true
        const message = l10n.getString("create-order-cost-centers-missing-message", { dimensionName })

        if (inputField?.isRequired) {
            openErrorDialog({
                title: l10n.getString("create-order-cost-centers-missing-dialog-title"),
                description: message,
            })
        } else {
            enqueueSnackbar({ variant: "warning", message })
        }
    }, [
        debouncedSearch,
        dimensionName,
        enabled,
        field.value,
        inputField?.isRequired,
        isError,
        isLoading,
        l10n,
        openErrorDialog,
        options,
    ])

    useEffect(() => {
        // Auto-fill the only cost center of the dimension. Keyed off the unfiltered result as well, so
        // a search that happens to return one match never selects it behind the user's back.
        if (hasSingleCostCenter && options.length === 1 && !field.value) {
            helpers.setValue(withDimensionName(options[0]))
        }
    }, [hasSingleCostCenter, field.value, helpers, options])

    useEffect(() => {
        // Guarded on `queryConfig`: the render bails out for a key this component cannot serve, and
        // such a field must not get a value written into the form behind an empty render.
        if (!queryConfig) return

        if (inputField?.predefined && !field.value) {
            helpers.setValue(
                withDimensionName({ id: String(inputField.predefined.value), name: inputField.predefined.text || "" }),
            )
        }
    }, [])

    useEffect(() => {
        setTooltipOpen(false)
    }, [field.value?.id])

    const handleChange = (_event: React.SyntheticEvent, value: AccountingDimensionOption | null) => {
        helpers.setValue(withDimensionName(value))
        if (isLoadOnOpen) {
            handleClearSearch()
        }
    }

    if (!queryConfig) return <></>

    return (
        <>
            <Tooltip
                title={field.value?.name ?? ""}
                open={tooltipOpen}
                onOpen={() => {
                    if (field.value && !open) setTooltipOpen(true)
                }}
                onClose={() => setTooltipOpen(false)}
            >
                <ApiQueryAutocomplete
                    // `isFetching` rather than `isLoading`: `keepPreviousData` keeps the query in a
                    // success state while the next search is in flight. `isSearchPending` covers the
                    // debounce window, so the spinner does not blink between keystrokes.
                    isLoading={enabled && (isFetching || isSearchPending)}
                    isError={isError}
                    options={options}
                    getOptionLabel={getOptionLabel}
                    value={field.value || null}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    size="small"
                    slotProps={{
                        textField: { ...formikErrorProps, InputLabelProps: { required: inputField?.isRequired } },
                    }}
                    label={label || description}
                    isOptionEqualToValue={isSameOption}
                    filterOptions={keepServerOrder}
                    readOnly={hasSingleCostCenter}
                />
            </Tooltip>

            <StandardErrorDialog dialogDetails={errorDialog} onClose={closeErrorDialog} />
        </>
    )
}
